import { MapContainer, TileLayer, CircleMarker, Polyline, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { GPSCoorindate } from "../../models/GPSCoorindate";
import { useMemo } from "react";
// Remove unused imports

interface Metric {
  id: string;
  name: string;
  description: string;
  unit: string;
}

interface EnhancedMapComponentProps {
  points: Array<GPSCoorindate & { metricValue?: number | null }>;
  selectedMetric: string;
  metrics: Metric[];
  height: string;
}

// Function to calculate dynamic ranges for speed and stability score
const calculateDynamicRanges = (
  data: Array<GPSCoorindate & { metricValue?: number | null }>,
  segmentCount: number = 5
): { min: number, max: number, ranges: number[] } => {
  // Filter out null values
  const validValues = data
    .map(point => point.metricValue)
    .filter((value): value is number => value !== null && value !== undefined);
  
  if (validValues.length === 0) {
    return { min: 0, max: 1, ranges: [0, 0.2, 0.4, 0.6, 0.8, 1] };
  }
  
  // Calculate min and max
  const min = Math.min(...validValues);
  const max = Math.max(...validValues);
  
  // Create range segments
  const step = (max - min) / segmentCount;
  const ranges = Array.from({ length: segmentCount + 1 }, (_, i) => min + step * i);
  
  return { min, max, ranges };
};

// Helper function to format values for legend display with metric-specific precision
const formatValue = (value: number, metric: string): string => {
  if (metric === "speed") {
    return value.toFixed(1); // One decimal place for speed
  } else if (metric === "gyro_stab") {
    return value.toFixed(2); // Two decimal places for stability score
  }
  return value.toString();
};

// Function to get color based on value and metric
const getColorForValue = (
  value: number | null, 
  metric: string,
  dynamicRanges?: { ranges: number[] }
): string => {
  if (value === null) return "#000000"; // Default black for no data

  // Take absolute value of value
  value = Math.abs(value);
  
  // Handle speed and stability score with dynamic ranges
  if ((metric === "speed" || metric === "gyro_stab") && dynamicRanges?.ranges.length) {
    const ranges = dynamicRanges.ranges;
    
    // Color gradient from green (low) to red (high)
    if (value < ranges[1]) return "#00FF00"; // Low - green
    if (value < ranges[2]) return "#88FF00"; // Light - light green
    if (value < ranges[3]) return "#FFFF00"; // Moderate - yellow
    if (value < ranges[4]) return "#FF8000"; // High - orange
    return "#FF0000"; // Very high - red
  }
  
  // Different color scales for different metrics
  switch (metric) {
    case "v_speed": // Wind speed
      // Blue (low) to red (high) gradient for wind speed
      if (value < 5) return "#0000FF"; // Low wind - blue
      if (value < 10) return "#00FFFF"; // Light wind - cyan
      if (value < 15) return "#00FF00"; // Moderate wind - green
      if (value < 20) return "#FFFF00"; // Strong wind - yellow
      if (value < 25) return "#FF8000"; // Very strong wind - orange
      return "#FF0000"; // Extreme wind - red
    
    case "temp": // Temperature
      // Blue (cold) to red (hot) gradient
      if (value < 0) return "#0000FF"; // Below freezing - blue
      if (value < 10) return "#00FFFF"; // Cold - cyan
      if (value < 20) return "#00FF00"; // Cool - green
      if (value < 25) return "#FFFF00"; // Warm - yellow
      if (value < 30) return "#FF8000"; // Hot - orange
      return "#FF0000"; // Very hot - red
    
    case "Tu": // Turbulence
      // Green (low) to red (high) gradient
      if (value < 0.2) return "#00FF00"; // Low turbulence - green
      if (value < 0.4) return "#88FF00"; // Light turbulence - light green
      if (value < 0.6) return "#FFFF00"; // Moderate turbulence - yellow
      if (value < 0.8) return "#FF8000"; // High turbulence - orange
      return "#FF0000"; // Severe turbulence - red
    
    case "hum": // Humidity
      // Yellow (dry) to blue (humid) gradient
      if (value < 20) return "#FFFF00"; // Very dry - yellow
      if (value < 40) return "#88FF00"; // Dry - light green
      if (value < 60) return "#00FF00"; // Moderate - green
      if (value < 80) return "#00FFFF"; // Humid - cyan
      return "#0000FF"; // Very humid - blue
    
    case "alpha_deg": // Alpha angle
    case "beta_deg": // Beta angle
      // Green (low) to red (high) gradient
      if (value < 5) return "#00FF00"; // Low angle - green
      if (value < 10) return "#88FF00"; // Light angle - light green
      if (value < 15) return "#FFFF00"; // Moderate angle - yellow
      if (value < 20) return "#FF8000"; // High angle - orange
      return "#FF0000"; // Very high angle - red

    default:
      // Default gradient for other metrics
      if (value < 0.2) return "#00FF00"; // Low - green
      if (value < 0.4) return "#88FF00"; // Light - light green
      if (value < 0.6) return "#FFFF00"; // Moderate - yellow
      if (value < 0.8) return "#FF8000"; // High - orange
      return "#FF0000"; // Very high - red
  }
};


export default function EnhancedMapComponent({
  points,
  selectedMetric,
  metrics,
  height,
}: EnhancedMapComponentProps) {
  // Helper functions to get metric info
  const getMetricInfo = (metricId: string) => {
    return metrics.find(m => m.id === metricId) || { id: metricId, name: metricId, description: '', unit: '' };
  };
  
  const getMetricLabel = (metricId: string): string => {
    return getMetricInfo(metricId).name;
  };
  
  const getMetricUnit = (metricId: string): string => {
    return getMetricInfo(metricId).unit;
  };
  const noDataContent = <h1>No data available</h1>;
  
  // Get start and end points safely
  const start = points && points.length > 0 ? points[0] : null;
  const end = points && points.length > 0 ? points[points.length - 1] : null;
  
  // Calculate dynamic ranges for speed and stability score
  const dynamicRanges = useMemo(() => {
    if (selectedMetric === "speed" || selectedMetric === "gyro_stab") {
      return calculateDynamicRanges(points);
    }
    return undefined;
  }, [points, selectedMetric]);
  
  // Create segments for color-coded path - moved before conditional returns
  const segments = useMemo(() => {
    // Return empty array if points are invalid
    if (!points || points.length < 2 || !points.every((point) => point.lat && point.lon)) {
      return [];
    }
    
    const result = [];
    
    // Group consecutive points with similar values
    let currentSegment: typeof points = [];
    let currentColor = "";
    
    for (let i = 0; i < points.length; i++) {
      const point = points[i];
      const color = getColorForValue(point.metricValue || null, selectedMetric, dynamicRanges);
      
      if (i === 0 || color !== currentColor) {
        // Start a new segment
        if (currentSegment.length > 0) {
          // Add the current point to the previous segment to avoid gaps
          // This creates an overlap point between segments
          if (i > 0) {
            currentSegment.push(point);
          }
          
          result.push({
            points: currentSegment,
            color: currentColor
          });
        }
        
        // Start the new segment
        // If this isn't the first point and we're changing color,
        // include the previous point in the new segment to avoid gaps
        if (i > 0 && color !== currentColor) {
          currentSegment = [points[i-1], point];
        } else {
          currentSegment = [point];
        }
        
        currentColor = color;
      } else {
        // Continue current segment
        currentSegment.push(point);
      }
    }
    
    // Add the last segment
    if (currentSegment.length > 0) {
      result.push({
        points: currentSegment,
        color: currentColor
      });
    }
    
    return result;
  }, [points, selectedMetric, dynamicRanges]);

  // Create legend items - moved before conditional returns
  const legendItems = useMemo(() => {
    const metricLabel = getMetricLabel(selectedMetric);
    const unit = getMetricUnit(selectedMetric);
    
    // Use dynamic ranges for speed and stability score
    if ((selectedMetric === "speed" || selectedMetric === "gyro_stab") && dynamicRanges?.ranges.length) {
      const ranges = dynamicRanges.ranges;
      
      return [
        { color: "#00FF00", label: `< ${formatValue(ranges[1], selectedMetric)} ${unit}` },
        { color: "#88FF00", label: `${formatValue(ranges[1], selectedMetric)}-${formatValue(ranges[2], selectedMetric)} ${unit}` },
        { color: "#FFFF00", label: `${formatValue(ranges[2], selectedMetric)}-${formatValue(ranges[3], selectedMetric)} ${unit}` },
        { color: "#FF8000", label: `${formatValue(ranges[3], selectedMetric)}-${formatValue(ranges[4], selectedMetric)} ${unit}` },
        { color: "#FF0000", label: `> ${formatValue(ranges[4], selectedMetric)} ${unit}` }
      ];
    }
    
    // Create different legend items based on the metric
    switch (selectedMetric) {
      case "v_speed":
        return [
          { color: "#0000FF", label: `< 5 ${unit}` },
          { color: "#00FFFF", label: `5-10 ${unit}` },
          { color: "#00FF00", label: `10-15 ${unit}` },
          { color: "#FFFF00", label: `15-20 ${unit}` },
          { color: "#FF8000", label: `20-25 ${unit}` },
          { color: "#FF0000", label: `> 25 ${unit}` }
        ];
      case "temp":
        return [
          { color: "#0000FF", label: `< 0 ${unit}` },
          { color: "#00FFFF", label: `0-10 ${unit}` },
          { color: "#00FF00", label: `10-20 ${unit}` },
          { color: "#FFFF00", label: `20-25 ${unit}` },
          { color: "#FF8000", label: `25-30 ${unit}` },
          { color: "#FF0000", label: `> 30 ${unit}` }
        ];
      case "Tu":
        return [
          { color: "#00FF00", label: "< 0.2" },
          { color: "#88FF00", label: "0.2-0.4" },
          { color: "#FFFF00", label: "0.4-0.6" },
          { color: "#FF8000", label: "0.6-0.8" },
          { color: "#FF0000", label: "> 0.8" }
        ];
      case "hum":
        return [
          { color: "#FFFF00", label: `< 20 ${unit}` },
          { color: "#88FF00", label: `20-40 ${unit}` },
          { color: "#00FF00", label: `40-60 ${unit}` },
          { color: "#00FFFF", label: `60-80 ${unit}` },
          { color: "#0000FF", label: `> 80 ${unit}` }
        ];
      case "alpha_deg":
      case "beta_deg":
        return [
          { color: "#00FF00", label: "< 5°" },
          { color: "#88FF00", label: "5-10°" },
          { color: "#FFFF00", label: "10-15°" },
          { color: "#FF8000", label: "15-20°" },
          { color: "#FF0000", label: "> 20°" }
        ];
      default:
        return [
          { color: "#00FF00", label: "Low" },
          { color: "#88FF00", label: "Light" },
          { color: "#FFFF00", label: "Moderate" },
          { color: "#FF8000", label: "High" },
          { color: "#FF0000", label: "Very High" }
        ];
    }
  }, [selectedMetric]);
  
  // Early returns after hooks
  if (!points || points.length === 0) {
    return noDataContent;
  }

  // Check if every point has the attributes lat and lon
  if (!points.every((point) => point.lat && point.lon)) {
    return noDataContent;
  }

  if (points.length < 2) {
    return noDataContent;
  }

  return (
    <div style={{ position: "relative" }}>
      <MapContainer
        style={{ height: height, width: "100%", borderRadius: "1em" }}
        center={[start!.lat, start!.lon]}
        zoom={13}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          opacity={0.5}
          className="map-tile"
        />

        {/* Render color-coded segments with increased weight and opacity */}
        {segments.map((segment, index) => (
          <Polyline
            key={`segment-${index}`}
            pathOptions={{ 
              color: segment.color,
              weight: 8, // Increased from 5 to make it more visible
              opacity: 1.0, // Increased from 0.8 for better visibility
              lineCap: "round", // Rounded line caps for better appearance
              lineJoin: "round" // Rounded line joins for better appearance
            }}
            positions={
              segment.points.map((point) => {
                return { lat: point.lat, lng: point.lon };
              })
            }
          >
            {segment.points.length > 0 && (
              <Popup>
                {getMetricLabel(selectedMetric)}: {
                  // Use a representative point from the middle of the segment
                  // For segments with overlap points (all except the first one), 
                  // we should skip the first point which belongs to the previous segment
                  segment.points.length > 1 && index > 0 
                    ? segment.points[1].metricValue 
                    : segment.points[0].metricValue
                } {getMetricUnit(selectedMetric)}
              </Popup>
            )}
          </Polyline>
        ))}

        {/* Start and end markers */}
        <CircleMarker
          center={{ lat: start!.lat, lng: start!.lon }}
          pathOptions={{ color: "#067E93", fillOpacity: 1 }}
          radius={10}
        >
          <Popup>Start</Popup>
        </CircleMarker>
        <CircleMarker
          center={{ lat: end!.lat, lng: end!.lon }}
          pathOptions={{ color: "white", fillColor: "#067E93", fillOpacity: 1 }}
          radius={10}
        >
          <Popup>End</Popup>
        </CircleMarker>
      </MapContainer>

      {/* Legend */}
      <div 
        style={{
          position: "absolute",
          bottom: "20px",
          right: "20px",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          padding: "10px",
          borderRadius: "5px",
          zIndex: 1000,
          color: "black"
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: "5px" }}>
          {getMetricLabel(selectedMetric)}
        </div>
        {legendItems.map((item, index) => (
          <div key={`legend-${index}`} style={{ display: "flex", alignItems: "center", marginBottom: "3px" }}>
            <div 
              style={{ 
                width: "20px", 
                height: "10px", 
                backgroundColor: item.color,
                marginRight: "5px"
              }} 
            />
            <div>{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
